// TODO STUDIO MIGRATION (Phase 5 prep): middleware now returns { jobId, room } for TTS avatar dispatches. Migrate from setInterval polling to useJobSocket(jobId, onEvent) from _editor/utils/jobSocket.js. See aidea-middleware spec docs/superpowers/specs/2026-05-25-producer-worker-discipline-design.md §7.

// Generic Socket.IO helper for JobBus-dispatched jobs.
//
// Mirrors the pattern in videoExportSocket.js but targets the /jobs namespace
// and subscribes to the per-job room emitted by JobManagerService (room = "job_<id>").
//
// Forwarded event names (emitted by JobManagerService / WebSocketGateway):
//   job_status_update    — { jobId, status }
//   job_progress_update  — { jobId, step, progress, message }
//   job_data_update      — { jobId, data }
//   job_completed        — { jobId, result }
//   job_failed           — { jobId, error }
//
// Each handler is called only when event.jobId === the subscribed jobId so
// multiple hooks sharing the same socket do not cross-contaminate.

import { useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { getToken } from "../../common/environment_config";
import { bearerSocketAuth } from "./socketAuth";

const apiBase = () => (window.ideaLibraryRootUrl || "").replace(/\/$/, "");

// Forwarded event names that the server emits inside a job room.
const JOB_EVENTS = [
  "job_status_update",
  "job_progress_update",
  "job_data_update",
  "job_completed",
  "job_failed",
];

// ─── Shared socket singleton ────────────────────────────────────────────────

let _sharedSocket = null;

function getSocket() {
  if (_sharedSocket && _sharedSocket.connected) return _sharedSocket;
  _sharedSocket = io(`${apiBase()}/jobs`, {
    transports: ["websocket"],
    auth: bearerSocketAuth(getToken),
    reconnection: true,
    reconnectionDelay: 500,
    reconnectionDelayMax: 5000,
  });
  return _sharedSocket;
}

// ─── Core connect fn (used by hook + imperative callers) ─────────────────────

/**
 * Connect to a per-job room and forward matching events to onEvent.
 *
 * The underlying socket is shared across calls so multiple concurrent
 * subscriptions do not open redundant connections. Returned cleanup fn
 * leaves the room but keeps the socket alive for other subscribers.
 *
 * @param {{ jobId: string, onEvent: (event: object) => void }} opts
 * @returns {Promise<() => void>} Cleanup / unsubscribe function.
 */
export async function connectJobSocket({ jobId, onEvent }) {
  const socket = getSocket();

  // Wait for connection
  await new Promise((resolve) => {
    if (socket.connected) return resolve();
    socket.once("connect", resolve);
  });

  // Join the job room on the server
  await new Promise((resolve, reject) => {
    socket.emit("job:join", { jobId }, (ack) => {
      if (ack && ack.ok) resolve();
      else reject(new Error(`job:join rejected for jobId=${jobId}`));
    });
  });

  // Attach per-event handlers; filter by jobId to avoid cross-contamination
  const handlers = {};
  for (const eventName of JOB_EVENTS) {
    const handler = (event) => {
      if (!event || event.jobId !== jobId) return;
      onEvent({ ...event, _event: eventName });
    };
    handlers[eventName] = handler;
    socket.on(eventName, handler);
  }

  // Return cleanup fn
  return () => {
    for (const eventName of JOB_EVENTS) {
      socket.off(eventName, handlers[eventName]);
    }
    socket.emit("job:leave", { jobId }, () => {});
  };
}

// ─── React hook: single job ──────────────────────────────────────────────────

/**
 * React hook that subscribes to a specific job's socket room.
 *
 * Connects on mount, cleans up on unmount or jobId change.
 *
 * @param {string | null} jobId  - BullMQ job ID returned by middleware.
 * @param {(event: object) => void} onEvent - Callback for each event.
 */
export function useJobSocket(jobId, onEvent) {
  const onEventRef = useRef(onEvent);
  useEffect(() => {
    onEventRef.current = onEvent;
  });

  useEffect(() => {
    if (!jobId) return;

    let cleanup = null;
    let cancelled = false;

    connectJobSocket({
      jobId,
      onEvent: (event) => onEventRef.current?.(event),
    }).then((unsubscribe) => {
      if (cancelled) {
        unsubscribe();
      } else {
        cleanup = unsubscribe;
      }
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [jobId]);
}

// ─── React hook: user-scoped job room (Phase 9 widget) ───────────────────────

/**
 * React hook that subscribes to the per-user job room.
 *
 * Used by the Phase 9 "active jobs" widget to receive updates for ALL of a
 * user's inflight jobs without knowing individual job IDs in advance.
 *
 * The room name emitted by the server is "user_<userId>" and it broadcasts
 * the same event names as per-job rooms.
 *
 * @param {string | null} userId  - JWT sub claim.
 * @param {(event: object) => void} onEvent - Callback for each event.
 */
export function useUserJobSocket(userId, onEvent) {
  const onEventRef = useRef(onEvent);
  useEffect(() => {
    onEventRef.current = onEvent;
  });

  useEffect(() => {
    if (!userId) return;

    const socket = getSocket();

    let cleanup = null;
    let cancelled = false;

    const connect = async () => {
      await new Promise((resolve) => {
        if (socket.connected) return resolve();
        socket.once("connect", resolve);
      });

      await new Promise((resolve, reject) => {
        socket.emit("job:join-user", { userId }, (ack) => {
          if (ack && ack.ok) resolve();
          else reject(new Error(`job:join-user rejected for userId=${userId}`));
        });
      });

      const handlers = {};
      for (const eventName of JOB_EVENTS) {
        const handler = (event) => {
          if (!event) return;
          onEventRef.current?.({ ...event, _event: eventName });
        };
        handlers[eventName] = handler;
        socket.on(eventName, handler);
      }

      return () => {
        for (const eventName of JOB_EVENTS) {
          socket.off(eventName, handlers[eventName]);
        }
        socket.emit("job:leave-user", { userId }, () => {});
      };
    };

    connect().then((unsubscribe) => {
      if (cancelled) {
        unsubscribe();
      } else {
        cleanup = unsubscribe;
      }
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [userId]);
}
